import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PublicLayout } from "@/pages/public/PublicSite";
import { Spinner } from "@/components/common";
import { Button } from "@/components/ui/button";
import { CalendarDays, Sparkles, Sun, FileText } from "lucide-react";

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];
const DOW = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function MonthGrid({ year, month, holidayMap }) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startCol = (first.getDay() + 6) % 7; // Monday=0
  const cells = [];
  for (let i = 0; i < startCol; i++) cells.push(null);
  for (let d = 1; d <= last.getDate(); d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  return (
    <div className="border rounded-lg bg-card p-4">
      <p className="text-sm font-bold uppercase tracking-wide text-secondary mb-3">{MONTHS[month]}</p>
      <div className="grid grid-cols-7 gap-1 text-[11px] text-muted-foreground mb-1">
        {DOW.map((d) => <div key={d} className="text-center font-semibold">{d}</div>)}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d == null) return <div key={i} />;
          const iso = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
          const h = holidayMap[iso];
          const isConcert = h?.holiday_type === "annual_concert";
          const isPub = h?.holiday_type === "public_holiday";
          const isYamaha = h?.holiday_type === "yamaha_school_holiday";
          return (
            <div key={i}
              title={h ? h.label : ""}
              data-testid={`cal-${iso}`}
              className={`h-9 rounded text-center text-xs flex items-center justify-center transition-colors
                ${isConcert ? "bg-[#52B788] text-white font-bold" :
                  isPub ? "bg-red-100 text-red-700 font-bold ring-1 ring-red-300" :
                  isYamaha ? "bg-blue-100 text-blue-700 font-semibold" :
                  "hover:bg-muted"}`}>
              {d}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function CalendarPage() {
  const [data, setData] = useState(null);
  const [rules, setRules] = useState(null);

  useEffect(() => {
    api.get("/public/school-calendar?year=2026").then((r) => setData(r.data)).catch(() => {});
    api.get("/public/school-rules").then((r) => setRules(r.data)).catch(() => {});
  }, []);

  if (!data) return <PublicLayout><div className="max-w-5xl mx-auto px-4 py-24"><Spinner /></div></PublicLayout>;

  const holidayMap = Object.fromEntries(data.holidays.map((h) => [h.date, h]));
  const publicList = data.holidays.filter((h) => h.holiday_type === "public_holiday");

  return (
    <PublicLayout>
      <div className="max-w-6xl mx-auto px-4 sm:px-8 py-14" data-testid="calendar-page">
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary mb-3">
          <Sparkles className="h-3.5 w-3.5" /> Cornerstone Music
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold mb-3">Academic Calendar {data.year}</h1>
        <p className="text-muted-foreground max-w-2xl mb-8">{data.note}</p>

        <div className="flex flex-wrap items-center gap-4 mb-6 text-xs" data-testid="calendar-legend">
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-blue-100 ring-1 ring-blue-200" />Yamaha School Holiday</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-red-100 ring-1 ring-red-300" />Public Holiday</span>
          <span className="inline-flex items-center gap-1.5"><span className="h-3 w-5 rounded bg-[#52B788]" />Annual Concert</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="calendar-grid">
          {Array.from({ length: 12 }).map((_, m) => (
            <MonthGrid key={m} year={data.year} month={m} holidayMap={holidayMap} />
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><Sun className="h-4 w-4 text-secondary" />Public Holidays Observed</h2>
            <ul className="space-y-1.5 text-sm" data-testid="public-holidays-list">
              {publicList.map((h) => (
                <li key={h.date} className="flex justify-between border-b border-border/60 pb-1.5">
                  <span className="text-muted-foreground">{new Date(h.date + "T00:00:00").toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}</span>
                  <span className="font-medium">{h.label}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-lg border bg-card p-6">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><CalendarDays className="h-4 w-4 text-secondary" />Annual School Concert</h2>
            <p className="text-3xl font-bold mb-2">{new Date(data.annual_concert_date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}</p>
            <p className="text-sm text-muted-foreground">A celebration of our students' progress over the year. Family and friends warmly welcome.</p>
            <Button asChild className="mt-4" size="sm"><Link to="/register">Enrol your child →</Link></Button>
          </div>
        </div>

        {rules && (
          <div className="rounded-lg border bg-card p-6 mb-16" data-testid="rules-card">
            <h2 className="text-lg font-bold mb-3 flex items-center gap-2"><FileText className="h-4 w-4 text-secondary" />School Rules &amp; Regulations</h2>
            <div className="text-sm leading-relaxed whitespace-pre-line text-foreground/85">{rules.rules}</div>
          </div>
        )}
      </div>
    </PublicLayout>
  );
}
